import { BasicButton, IOSSwitch, InputTextArea } from "@/components";
import { Typography } from "@/components/text";
import useBackward from "@/hooks/useBackward";
import { useBoolean } from "@/hooks/useBoolean";
import useReminderMutation from "@/hooks/useReminderMutation";
import type { ReminderFormFieldSchema } from "@/schemas";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Modal from "@mui/material/Modal";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDateTimePicker } from "@mui/x-date-pickers/StaticDateTimePicker";
import dayjs from "dayjs";
import omit from "lodash/omit";
import {
	Controller,
	type SubmitHandler,
	useFormContext,
} from "react-hook-form";
import { SelectElement } from "react-hook-form-mui";

const repeatOptions = ["Never", "Daily", "Weekly", "Monthly", "Yearly"].map(
	(rep) => ({
		id: rep,
		label: rep,
	}),
);

const RemminderForm = () => {
	const onBack = useBackward();
	const form = useFormContext<ReminderFormFieldSchema>();
	const isEdit = !!form.watch("id");
	const isRemind = form.watch("remind_me");

	const {
		value: isOpenDateTimePicker,
		setTrue: openDateTimePicker,
		setFalse: closeDateTimePicker,
	} = useBoolean();

	const { create, update } = useReminderMutation();

	const onSubmit: SubmitHandler<ReminderFormFieldSchema> = (data, e) => {
		e?.stopPropagation();
		e?.preventDefault();
		if (!data.remind_me) {
			Object.assign(data, {
				repeat: "Never",
				alarm: undefined,
			});
		}
		if (data.id) {
			return update.mutate(data, {
				onSuccess: () => {
					onBack();
				},
			});
		}
		return create.mutate(data, {
			onSuccess: () => {
				onBack();
			},
		});
	};

	return (
		<form
			onSubmit={form.handleSubmit(onSubmit)}
			className="flex flex-col gap-3"
		>
			<Card className="!rounded-xl">
				<CardContent className="flex flex-col !p-4 gap-2">
					<Controller
						control={form.control}
						name="name"
						render={({ field }) => (
							<input
								type="text"
								className="appearance-none border-none !outline-none"
								placeholder="Input title here.."
								{...field}
							/>
						)}
					/>
				</CardContent>
			</Card>
			<Card className="!rounded-xl">
				<CardContent className="flex flex-col !p-4 gap-2">
					<Controller
						control={form.control}
						name="remind_me"
						render={({ field }) => (
							<div>
								<div className="flex flex-row justify-between items-center py-2">
									<Typography fontWeight={500} fontSize={14}>
										Remind me on a day
									</Typography>
									<IOSSwitch
										{...omit(field, ["ref", "onChange", "value"])}
										checked={field.value}
										onChange={(e, checked) => {
											field.onChange(e, checked);
											if (!isEdit && !checked) {
												form.resetField("alarm");
												form.resetField("repeat");
											}
										}}
									/>
								</div>
							</div>
						)}
					/>
					<Divider />
					<Controller
						control={form.control}
						name="alarm"
						disabled={!isRemind}
						render={({ field }) => (
							<>
								{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
								<div
									className="flex flex-row justify-between items-center py-2 data-[disabled=true]:pointer-events-none"
									data-disabled={field.disabled}
									onClick={openDateTimePicker}
								>
									<Typography fontWeight={500} fontSize={14}>
										Alarm
									</Typography>
									<Typography
										fontWeight={400}
										fontSize={14}
										data-disabled={field.disabled}
										className="cursor-pointer data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50"
									>
										{!field.value
											? "Select Date and Time"
											: dayjs(field.value).format("MMM, DD/MM/YYYY, HH:mm A")}
									</Typography>
								</div>
								<Modal
									open={isOpenDateTimePicker}
									onClose={closeDateTimePicker}
								>
									<div className="wrapper bg-white rounded-2xl !max-w-[30rem] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
										<div className="relative p-4">
											<LocalizationProvider dateAdapter={AdapterDayjs}>
												<StaticDateTimePicker
													defaultValue={dayjs(new Date())}
													onAccept={(val) => {
														field.onChange(val);
														closeDateTimePicker();
													}}
													value={field.value ? dayjs(field.value) : null}
													disabled={field.disabled}
												/>
											</LocalizationProvider>
										</div>
									</div>
								</Modal>
							</>
						)}
					/>
					<Divider />
					<div className="flex flex-row justify-between items-center py-2">
						<Typography fontWeight={500} fontSize={14}>
							Repeat
						</Typography>
						<SelectElement
							control={form.control}
							name="repeat"
							options={repeatOptions}
							slotProps={{
								htmlInput: { "aria-label": "repeat" },
							}}
						/>
					</div>
				</CardContent>
			</Card>
			<Card className="!rounded-xl">
				<CardContent className="flex flex-col !p-4 gap-2">
					<Typography fontWeight={700} fontSize={14}>
						Notes
					</Typography>
					<Controller
						control={form.control}
						name="notes"
						render={({ field }) => (
							<InputTextArea
								fullWidth
								data-label="Notes"
								rows={3}
								placeholder="Add Notes"
								{...omit(field, ["ref"])}
							/>
						)}
					/>
				</CardContent>
			</Card>

			<Box className="fixed bg-white shadow bottom-0 inset-x-0 z-50 py-3">
				<Box className="wrapper mx-auto">
					<BasicButton
						type="submit"
						variant="contained"
						color="secondary"
						className="w-full !h-12 !text-sm font-semibold !shadow-none"
						disabled={!form.formState.isValid}
						loading={form.formState.isSubmitting}
					>
						Save
					</BasicButton>
				</Box>
			</Box>
		</form>
	);
};

export default RemminderForm;
